'use client'

import { Fragment } from 'react'

import { Box, Checkbox, ContentText, IconCheck, spacing, useToggle } from '@fortum/elemental-ui'

import { AddonPriceSection } from '@/open-web/components/UpgradeFlow/AddonPriceSection'
import { Icon } from '@/shared/components/Icon'
import type { CustomerType } from '@/shared/graphql/schema/commonBackend/graphql'
import { useTheme } from '@/shared/hooks/useTheme'
import type { EnrichedAvailableAddon } from '@/shared/services/campaignDataResolver'
import type { SelectedAddons } from '@/shared/store/slices/selectedContractSlice'
import type { GlobalAddonItem } from '@/shared/utils/addonsUtils'

import { HourlyMeasurementModal } from '../HourlyMeasurementModal'

export type IconColorVariant = 'primary' | 'secondary'

export type AddonSectionProps = {
  /**
   * Addon items which should be presented
   */
  addons: GlobalAddonItem[]

  /**
   * VoidFunction triggered whenver one of the toggles change state
   */
  onChange: (addon: GlobalAddonItem) => void
  /**
   * List of pre selected addons tariffNos
   */
  selectedAddons?: SelectedAddons

  /**
   * If true, toggles are disabled and showing the check icon instead
   */
  isAddonToggleHidden?: boolean

  /**
   * Customer type
   */
  customerType?: CustomerType
}

/**
 * Section which present bundled or pre-selected Addons
 */
export const AddonSection = ({
  addons,
  customerType,
  onChange,
  selectedAddons,
  isAddonToggleHidden,
}: AddonSectionProps) => {
  const theme = useTheme()
  const [isHourlyMeasurementModalOpened, toggleIsHourlyMeasurementModalOpened] = useToggle(false)

  const handleHourlyMeasurementModalConfirm = (addon: GlobalAddonItem) => {
    onChange(addon)
    toggleIsHourlyMeasurementModalOpened()
  }

  const getIsAddonToggleHidden = (addon: GlobalAddonItem) => {
    return isAddonToggleHidden && !addon.options.isHourlyMeasurementAddon
  }

  const handleClick = (addon: GlobalAddonItem) => {
    const isSelected = selectedAddons?.[addon.addonType]?.value === addon.tariffNo
    if (!addon.options.isBundled) {
      //Open dialog if hourly measurement addon is not selected, otherwise deselect it immediately
      if (addon.options.isHourlyMeasurementAddon && !isSelected) {
        toggleIsHourlyMeasurementModalOpened()
      } else {
        onChange(addon)
      }
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={{ default: spacing.xxs, s: spacing.xs }}>
      {addons.map((addon) => {
        return (
          <Fragment key={addon.tariffNo}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              cursor={getIsAddonToggleHidden(addon) ? 'default' : 'pointer'}
            >
              <Box display="flex" gap={{ default: spacing.xxs, m: spacing.xs }}>
                {addon.options.isBundled || getIsAddonToggleHidden(addon) ? (
                  <Icon icon={IconCheck} color={theme.colors.borderPositive} />
                ) : (
                  <Checkbox
                    color={theme.colors.borderPositive}
                    checked={selectedAddons?.[addon.addonType]?.value === addon.tariffNo}
                    data-testid={`addon-toggle-${addon.tariffNo}`}
                    aria-label={addon.enrichedData.cfData.title || ''}
                    borderRadius={spacing.xxxxs}
                    onChange={(e) =>
                      getIsAddonToggleHidden(addon) ? e.preventDefault() : handleClick(addon)
                    }
                    marginVertical={spacing.xxxxs}
                    label={
                      <ContentText
                        ml={{ default: 0, m: spacing.xxxs }}
                        tag="label"
                        size="m"
                        color={theme.colors.borderPositive}
                      >
                        {addon.enrichedData.cfData.title}
                      </ContentText>
                    }
                  />
                )}

                {!addon.options.isHourlyMeasurementAddon &&
                  (addon.options.isBundled || getIsAddonToggleHidden(addon)) && (
                    <AddonPriceSection
                      addon={addon.enrichedData as EnrichedAvailableAddon}
                      showLabel
                      customerType={customerType}
                      boxProps={{
                        alignContent: 'left',
                      }}
                      colors={{
                        textPrice: theme.colors.textPositive,
                        textPrimary: theme.colors.textPrimary,
                        textPositive: theme.colors.borderPositive,
                      }}
                      priceLabelFontSize="m"
                      priceStrikeThroughSize="xs"
                    />
                  )}
              </Box>
            </Box>

            {addon.options.isHourlyMeasurementAddon && (
              <HourlyMeasurementModal
                isOpened={isHourlyMeasurementModalOpened}
                onCancel={toggleIsHourlyMeasurementModalOpened}
                onConfirm={() => handleHourlyMeasurementModalConfirm(addon)}
                content={addon.enrichedData.cfData.additionalInformation}
              />
            )}
          </Fragment>
        )
      })}
    </Box>
  )
}
